import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { CmsConfiguration } from '@core/domain/models/cms-configuration.model';
import { CmsRestConfigService } from '@core/infrastructure/cms-rest/config/config.service';
import { DatesService } from '@core/services/date/dates.use-cases';
import {
    CountdownDate,
    CountDownRender,
    TypeOfDate,
} from '@features/cms-render/ui/render-pieces/piece-countdown/piece-countdown.model';
import { DuttiDateFormat, DuttiDate } from '@frontend/common/duttidate';
import { isDefined } from '@frontend/common/utils';

@Injectable()
export class CountdownService {
    public nowDate: DuttiDate;

    public timezone: string;

    private readonly isServiceReady$: Subject<string> = new Subject();

    public isServiceReady = this.isServiceReady$.asObservable();

    public startDate: DuttiDate;

    public endDay: DuttiDate;

    public percentLeft: string = '';

    private countDownValues: CountdownDate | any;

    private countryCode: string = 'ES';

    private configs: CmsConfiguration[] = [];

    private typeOfDate: string | undefined;

    constructor(
        private readonly apiCmsConfigurationsService: CmsRestConfigService,
    ) {}

    public initializeService(
        typeOfDateVal: number,
        configurationName: string,
        countryCode: string,
        currentDate: DuttiDate,
        countDownValues: CountdownDate
    ): void {
        this.typeOfDate = TypeOfDate[typeOfDateVal];

        if (this.typeOfDate !== 'spain') {
            this.countryCode = countryCode;
        }

        const dataString = currentDate.format(DuttiDateFormat.ISO_NO_UTC);
        this.timezone = DatesService.getTimeZoneFromCountry(this.countryCode);

        this.setCurrentDate(dataString, currentDate);

        this.countDownValues = countDownValues;
        this.initDates();

        if (this.typeOfDate === 'configuration') {
            this.getConfigurations(configurationName);
        } else {
            this.isServiceReady$.next(undefined);
        }
    }

    public setCurrentDate(dataString: string, currentDate: DuttiDate) {
        if (isDefined(dataString)) {
            this.nowDate = new DuttiDate(dataString, DuttiDateFormat.ISO_NO_UTC, this.timezone);
        } else {
            this.nowDate = currentDate;
        }
    }

    private getConfigurations(configurationName: string): void {
        this.apiCmsConfigurationsService
            .getConfigurations(this.countryCode)
            .subscribe((response: CmsConfiguration[]) => {
                this.configs = response;
                const dateConfig = this.findConfiguration(configurationName);
                this.isServiceReady$.next(dateConfig);
            });
    }

    private initDates(): void {
        const dateStartSetting = `${this.countDownValues.startDate}T00:00:00`;
        if (this.typeOfDate === 'configuration') {
            this.endDay = new DuttiDate(this.countDownValues.endDate, DuttiDateFormat.ISO_NO_UTC, this.timezone);
        } else {
            const configHour = this.countDownValues.fromHour ?? 0;
            const configMinutes = this.countDownValues.fromMinutes ?? 0;

            const hour = configHour < 10 ? `0${configHour}` : configHour;
            const minutes = configMinutes < 10 ? `0${configMinutes}` : configMinutes;
            const dateEndSetting = `${this.countDownValues.endDate}T${hour}:${minutes}:00`;
            this.endDay = new DuttiDate(dateEndSetting, DuttiDateFormat.ISO_NO_UTC, this.timezone);
        }
        this.startDate = new DuttiDate(dateStartSetting, DuttiDateFormat.ISO_NO_UTC, this.timezone);
    }

    public getTimeDifference() {
        const durationFromTodayToDday = this.endDay.getDurationBetweenDates(this.nowDate.add(1, 'seconds'));
        const durationWithoutOneSecond = durationFromTodayToDday.subtract(1, 'second');
        this.percentLeft = this.calculatePercentLeft();
        return durationWithoutOneSecond;
    }

    public generateCountDownRender(timeDifference: any, onlyHours: boolean): CountDownRender {
        const countDownRender: CountDownRender = {
            isOnTime: true,
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
        };
        const seconds: number = timeDifference.seconds();
        const minutes: number = timeDifference.minutes();
        let hours: number = timeDifference.hours();
        countDownRender.secondsView = seconds < 10 ? `0${seconds}` : `${seconds}`;
        countDownRender.minutesView = minutes < 10 ? `0${minutes}` : `${minutes}`;
        if (onlyHours) {
            hours += timeDifference.days() * 24;
        } else {
            countDownRender.daysView = `${timeDifference.days()}`;
        }
        countDownRender.hoursView = hours < 10 ? `0${hours}` : `${hours}`;

        countDownRender.isOnTime = this.isDateOnTime(timeDifference.days(), hours, minutes, seconds);
        if (!countDownRender.isOnTime) {
            countDownRender.secondsView = '0';
        }

        countDownRender.seconds = seconds;
        countDownRender.minutes = minutes;
        countDownRender.hours = hours;
        countDownRender.days = timeDifference.days();
        return countDownRender;
    }

    public isDateOnTime(days: number, hours: number, minutes: number, seconds: number): boolean {
        if (days > 0) {
            return true;
        }
        if (hours > 0) {
            return true;
        }
        if (minutes > 0) {
            return true;
        }
        if (seconds > 0) {
            return true;
        }
        return false;
    }

    private calculatePercentLeft(): string {
        const percentile = 100.0 * this.nowDate.diff(this.startDate) / this.endDay.diff(this.startDate);
        return Math.round(percentile).toString();
    }

    private findConfiguration(key: string): any {
        if (isDefined(this.configs) && this.configs.length > 0) {
            const attachment = this.configs.filter((item: any) => {
                return item.key === key;
            });
            if (!!attachment[0]) {
                return attachment[0].webApp;
            }
            return [];
        }
        return [];
    }
}
